/**
 * Servicio de pedidos — Portal Cliente Menuee
 *
 * Responsabilidad: crear pedidos exclusivamente vía rpc_crear_pedido (E6).
 *
 * Consume: rpcClient, mappers/pedidoMapper.
 *
 * Qué nunca debe hacer:
 * - INSERT directo en pedidos / pedido_items.
 * - Invocar E2, E3, E4, E5 (cobros y caja son solo Admin).
 * - Enviar subtotal, total, delivery ni propinas en el payload de creación.
 * - Contener lógica visual ni JSX.
 */

import { callRpc, unwrapRpcData } from "./rpcClient";
import {
  mapCarritoToE6Payload,
  mapE6PayloadToRpcParams,
  mapPedidoE6ResponseToView,
  buildNotasPedido,
} from "../mappers/pedidoMapper";

/** Nombre del RPC oficial de creación de pedidos (E6). */
export const RPC_CREAR_PEDIDO = "rpc_crear_pedido";

/**
 * @typedef {import('../hooks/useCarrito').CarritoItem} CarritoItem
 */

/**
 * @typedef {Object} CreatePedidoInput
 * @property {string} sucursalId
 * @property {CarritoItem[]} carrito
 * @property {'delivery'|'retiro'|string} canal
 * @property {{ nombre: string, telefono: string }} cliente
 * @property {Object|null} [direccion]
 * @property {string|null} [horario]
 * @property {string} idempotencyKey
 */

/**
 * Crea un pedido mediante E6 (única vía permitida en Portal).
 *
 * @param {CreatePedidoInput} input
 * @returns {Promise<import('../mappers/pedidoMapper').PedidoConfirmacionView>}
 */
export async function createPedido(input) {
  const {
    sucursalId,
    carrito,
    canal,
    cliente,
    direccion = null,
    horario = null,
    idempotencyKey,
  } = input;

  if (!sucursalId) {
    throw new Error("createPedido: sucursalId es obligatorio");
  }

  if (!idempotencyKey) {
    throw new Error("createPedido: idempotencyKey es obligatorio");
  }

  const payload = mapCarritoToE6Payload({
    carrito,
    canal,
    cliente,
    direccion,
  });

  const notas = buildNotasPedido({
    horario,
    referencias: direccion?.referencias,
    comentarios: direccion?.comentarios,
  });

  const rpcParams = mapE6PayloadToRpcParams({
    sucursalId,
    payload,
    estadoInicial: "pendiente",
    notas,
  });

  const envelope = await callRpc(RPC_CREAR_PEDIDO, rpcParams, {
    idempotencyKey,
    idempotencyParam: "p_idempotency_key",
  });

  const data = unwrapRpcData(envelope);
  const pedido = mapPedidoE6ResponseToView(data);

  if (!pedido) {
    throw new Error("createPedido: respuesta E6 inválida");
  }

  return pedido;
}

/** Alias compatible con guía Menuee Admin. */
export const createPedidoConItems = createPedido;

/**
 * @param {string} _pedidoId
 * @returns {Promise<null>}
 */
export async function getPedidoPorId(_pedidoId) {
  throw new Error("getPedidoPorId: pendiente implementación tracking");
}
